import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import logger from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google logo SVG (official brand colors)
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Register = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { register, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company_name: '',
    role: null,
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordValid, setPasswordValid] = useState(false);
  const [checkingPassword, setCheckingPassword] = useState(false);

  // Debounced strength + breach check against /auth/validate-password
  useEffect(() => {
    if (formData.password.length < 4) {
      setPasswordErrors([]);
      setPasswordValid(false);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingPassword(true);
      try {
        const res = await axios.post(`${API}/auth/validate-password`, {
          password: formData.password,
        });
        const errs = res.data.errors || [];
        setPasswordErrors(errs);
        setPasswordValid(res.data.valid);
        if (res.data.is_breached) {
          setPasswordErrors([
            ...errs,
            t('landing.registerModal.passwordBreached', { count: (res.data.breach_count || 0).toLocaleString() }),
          ]);
          setPasswordValid(false);
        }
      } catch (err) {
        logger.error('Password validation failed:', err);
      } finally {
        setCheckingPassword(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.password, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role) {
      toast.error(t('landing.registerModal.selectRoleFirst') || (isFr ? 'Veuillez d\'abord sélectionner votre type de compte ci-dessous' : 'Please select your account type below first'));
      return;
    }
    if (!passwordValid) {
      toast.error(t('landing.registerModal.passwordWeak'));
      return;
    }
    setLoading(true);
    try {
      const result = await register(formData);

      // Backend enforces email verification before first login.
      if (result?.verification_required) {
        toast.success(
          isFr
            ? 'Compte créé. Veuillez vérifier votre courriel pour continuer.'
            : 'Account created. Please check your email to verify your account.'
        );
        setTimeout(() => navigate('/verify-email-sent', { replace: true }), 100);
        return;
      }

      toast.success(t('landing.registerModal.accountCreated') || 'Account created successfully!');
      setTimeout(() => {
        navigate(result?.role === 'admin' ? '/admin/dashboard' : '/dashboard', { replace: true });
      }, 100);
    } catch (error) {
      if (error?.code === 'auto_signin_unavailable') {
        toast.success(
          t('landing.registerModal.accountCreatedSignInRequired') ||
            (isFr
              ? 'Compte créé. Veuillez vous connecter pour continuer.'
              : 'Account created. Please sign in to continue.')
        );
        setTimeout(() => navigate('/login', { replace: true }), 100);
        return;
      }
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        setPasswordErrors(detail);
        toast.error(t('landing.registerModal.passwordWeak'));
      } else if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        setPasswordErrors(detail.errors);
        toast.error(detail.message || t('landing.registerModal.passwordWeak'));
      } else {
        toast.error(detail || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-primary rounded-lg mx-auto mb-4 flex items-center justify-center">
              <span className="text-white text-2xl font-bold">P</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isFr ? 'Créer un compte' : 'Create an account'}
            </h1>
            <p className="text-gray-600">
              {isFr ? 'Rejoignez ProposAI pour gérer vos appels à soumission' : 'Join ProposAI to manage your RFPs'}
            </p>
          </div>

          {/* Google OAuth button */}
          <button
            type="button"
            onClick={async () => {
              sessionStorage.setItem('pendingSignupRole', JSON.stringify({ role: formData.role, ts: Date.now() }));
              try {
                await loginWithGoogle();
              } catch (error) {
                toast.error(t('auth.googleOAuthFailed'));
              }
            }}
            disabled={!formData.role}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            data-testid="google-register-btn"
            title={!formData.role ? (t('landing.registerModal.selectRoleFirst') || (isFr ? 'Veuillez d\'abord sélectionner votre type de compte ci-dessous' : 'Please select your account type below first')) : undefined}
          >
            <GoogleIcon />
            {t('landing.registerModal.googleSignUp')}
          </button>
          {!formData.role && (
            <p className="text-xs text-muted-foreground text-center mt-2 mb-3" data-testid="google-role-hint">
              {t('landing.registerModal.selectRoleFirst') || (isFr ? 'Veuillez d\'abord sélectionner votre type de compte ci-dessous' : 'Please select your account type below first')}
            </p>
          )}
          <div className="mb-5" />

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-muted-foreground">{isFr ? 'ou' : 'or'}</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t('landing.registerModal.firstName') || 'Prénom'}</Label>
                <Input
                  id="first_name"
                  data-testid="register-first-name-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t('landing.registerModal.lastName') || 'Nom'}</Label>
                <Input
                  id="last_name"
                  data-testid="register-last-name-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="company_name">
                {t('landing.registerModal.companyName') || 'Nom de l\'entreprise'}
                {formData.role !== 'business' && (
                  <span className="text-muted-foreground text-xs ml-2">
                    ({isFr ? 'facultatif' : 'optional'})
                  </span>
                )}
              </Label>
              <Input
                id="company_name"
                data-testid="register-company-input"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required={formData.role === 'business'}
              />
            </div>

            <div>
              <Label htmlFor="email">{t('landing.registerModal.workEmail') || 'Courriel professionnel'}</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="password">{t('landing.registerModal.password') || 'Mot de passe'}</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="register-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={12}
                  placeholder={t('landing.registerModal.passwordPlaceholder')}
                  className={`pr-10 ${
                    formData.password.length > 0
                      ? passwordValid
                        ? 'border-green-500 focus-visible:ring-green-500'
                        : 'border-red-500 focus-visible:ring-red-500'
                      : ''
                  }`}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  tabIndex={-1} data-testid="toggle-register-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.password.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="register-password-feedback">
                  {checkingPassword ? (
                    <p className="text-sm text-muted-foreground">{t('landing.registerModal.passwordChecking')}</p>
                  ) : passwordValid ? (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {t('landing.registerModal.passwordStrong')}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {passwordErrors.map((error) => (
                        <p key={error} className="text-sm text-red-600 flex items-center gap-1">
                          <XCircle className="w-4 h-4 flex-shrink-0" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>{t('landing.registerModal.roleLabel')}</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'business' })}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${formData.role === 'business' ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:bg-muted/50'}`}
                  data-testid="role-business-btn"
                >
                  {t('landing.registerModal.roleBusiness')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'vendor' })}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${formData.role === 'vendor' ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:bg-muted/50'}`}
                  data-testid="role-vendor-btn"
                >
                  {t('landing.registerModal.roleVendor')}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading || !formData.role || !passwordValid}
              data-testid="register-submit-btn"
            >
              {loading ? (t('common.loading') || 'Chargement...') : (t('landing.registerModal.createAccount') || 'Créer un compte')}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            {t('landing.registerModal.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
              {t('landing.registerModal.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
